import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
	CHAT_BLOCK_SCHEMA_VERSION,
	type ChatBlock,
} from "../components/ChatBar/blocks/chat-block.types";
import { safeParseBlock } from "../components/ChatBar/blocks/safeParseBlock";
import {
	fetchTranscript,
	normalizeTranscriptMessage,
} from "../services/assistantHandoffApi";
import type { IChatActionProposal } from "../services/chatActionsApi";
import {
	type ChatSession,
	type StreamingChatEvent,
	type UserChatRequest,
	type UserChatResponse,
	userChatApi,
} from "../services/userChatApi";
import { useCompanyStore } from "../store/company.store";
import type {
	ChatAuthor,
	IChatMessageSocketEvent,
} from "../types/assistant-handoff.types";
import { watchChatSocket } from "../utils/chatSocketWatch";
import {
	connectToWebSocket,
	GATEWAY_TYPES,
	getSocket,
	offSocketEvent,
	onSocketEvent,
} from "../utils/websocket";
import {
	appendIncomingMessage,
	dropEmptyPlaceholder,
	isHandoffOpen,
	mergeLoadedTranscript,
} from "./chatHandoffState";
import { useChatHandoff } from "./useChatHandoff";

// Persisted chat session id, SCOPED BY COMPANY CONTEXT. The recon-ai SESSION
// agent binds its data capability from the company present when the session's
// first turn runs; a session born in the global (no-company) context and later
// reused inside a company is more likely to answer data questions like a generic
// chatbot ("I can't access your data"). Keying the stored session id by company
// guarantees a session used inside company X was created while in X's context,
// so data starters run grounded from turn 1.
//
// recon-ai also REFUSES a session continued under another company, so the key
// must be the company the request actually carries: the route company, else
// the one in the company store (what `userChatApi.sendMessageStream` sends).
// Keying on the route alone left one "global" session shared by every company
// on non-company routes (cross-repo-reviewer#2).
const sessionKey = (companyId?: number | null): string =>
	companyId != null ? `chat-session-id:${companyId}` : "chat-session-id:global";

export interface ChatMessage {
	id: string;
	// `staff` is a person from Equility who joined via the live handoff;
	// `system` is an injected notice ("Dana from Equility joined the chat").
	role: ChatAuthor;
	content: string;
	timestamp: string;
	// Byline for a `staff` message.
	staffName?: string;
	// Notice type for a `system` message (staff_joined, staff_left, ...).
	kind?: string;
	// True when this assistant bubble holds a failure notice rather than a real
	// response (e.g. the data-tool stream timed out). Kept in the transcript so
	// the failed turn stays visible instead of vanishing behind a toast.
	isError?: boolean;
	metadata?: {
		used_knowledge_base?: boolean;
		documents_found?: number;
		generation_time_ms?: number;
	};
	// Typed content blocks (citation/table/chart/…) streamed alongside the text
	// via `structured_data` events. Rendered after the message text.
	blocks?: ChatBlock[];
}

/**
 * @param companyId Route-resolved company id for the current context. When the
 * user is inside a company, ChatBar passes it so the backend can mint the
 * transaction-data service token (the SESSION agent then answers data
 * questions). Falls back to the persisted company store inside `userChatApi`
 * when omitted.
 */
export function useUserChat(companyId?: number | null) {
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isStreaming, setIsStreaming] = useState(false);
	const [sessionId, setSessionId] = useState<string | undefined>();
	const storeCompanyId = useCompanyStore((state) => state.id);
	// Same precedence as the `company_id` the stream request sends.
	const sessionCompanyId = companyId ?? storeCompanyId;
	const [sessions, setSessions] = useState<ChatSession[]>([]);
	const [isHealthy, setIsHealthy] = useState(true);
	const abortControllerRef = useRef<AbortController | null>(null);
	const wsConnectedRef = useRef(false);
	// The server closed this chat (409, a closed-session turn, or expiry).
	// Sending again would only fail again, so the UI offers a new chat.
	const [isSessionEnded, setIsSessionEnded] = useState(false);
	const markSessionEnded = useCallback(() => {
		setIsSessionEnded(true);
	}, []);
	useEffect(() => {
		setIsSessionEnded(false);
	}, [sessionId]);
	const handoffState = useChatHandoff(sessionId, companyId, {
		onSessionEnded: markSessionEnded,
	});
	const isHumanActiveRef = useRef(false);
	isHumanActiveRef.current = handoffState.isHumanActive;

	const sessionIdRef = useRef(sessionId);
	sessionIdRef.current = sessionId;
	const { refreshHandoff, applyServerHandoff, clearHandoffError } =
		handoffState;

	// Load the stored transcript for the active session and merge it by id with
	// what is on screen. Without this a reload emptied the chat, which is
	// unworkable once a person may be mid-reply. A brand-new session 404s
	// upstream; that is just an empty conversation. A response for a session
	// the user has since left is dropped.
	const refreshTranscript = useCallback(async () => {
		const requestedFor = sessionIdRef.current;
		if (!requestedFor) return;
		try {
			const loaded = await fetchTranscript(requestedFor, companyId ?? null);
			if (sessionIdRef.current !== requestedFor) return;
			setMessages((current) => mergeLoadedTranscript(current, loaded));
		} catch (error) {
			console.warn("[useUserChat] Transcript not loaded:", error);
		}
	}, [companyId]);

	useEffect(() => {
		void refreshTranscript();
	}, [sessionId, refreshTranscript]);

	// A dropped socket misses every push sent while it was down (a staff reply,
	// the handoff being claimed). On each reconnect, re-register the session and
	// refetch both, merging. The first connection is covered by the effects above.
	useEffect(() => {
		if (!sessionId) return;
		return watchChatSocket((socket, isReconnect) => {
			if (!isReconnect) return;
			socket.emit("chat:session:start", { sessionId });
			void refreshHandoff();
			void refreshTranscript();
		});
	}, [sessionId, refreshHandoff, refreshTranscript]);

	// Messages pushed by the API while a handoff is open: a staff member's
	// reply or a system notice. Only this session's messages are taken.
	useEffect(() => {
		if (!sessionId) return;
		const handlePushedMessage = (payload: IChatMessageSocketEvent) => {
			if (payload?.sessionId !== sessionId) return;
			const incoming = normalizeTranscriptMessage(payload.message);
			if (!incoming) return;
			setMessages((current) => appendIncomingMessage(current, incoming));
		};
		onSocketEvent("chat:message", handlePushedMessage, GATEWAY_TYPES.CHAT);
		return () => {
			offSocketEvent("chat:message", handlePushedMessage, GATEWAY_TYPES.CHAT);
		};
	}, [sessionId]);

	// Load or generate session ID on mount
	useEffect(() => {
		const savedSessionId = localStorage.getItem(sessionKey(sessionCompanyId));
		if (savedSessionId) {
			setSessionId(savedSessionId);
		} else {
			const newSessionId = uuidv4();
			setSessionId(newSessionId);
			localStorage.setItem(sessionKey(sessionCompanyId), newSessionId);
		}

		loadSessions();
		checkHealth();
		// Re-run on company change so the persisted session id is always the one
		// scoped to the CURRENT company (sessionKey), never a session inherited
		// from the global/no-company context or a different company.
	}, [sessionCompanyId]);

	// WebSocket session tracking - connect and register session
	useEffect(() => {
		if (!sessionId) return;

		const setupWebSocket = async () => {
			try {
				const socket = await connectToWebSocket(GATEWAY_TYPES.CHAT);
				if (socket) {
					wsConnectedRef.current = true;
					// Register the active session with the gateway
					socket.emit("chat:session:start", { sessionId });
					console.log(
						"[useUserChat] Registered session with WebSocket:",
						sessionId,
					);
				}
			} catch (error) {
				console.warn(
					"[useUserChat] Failed to connect to chat WebSocket:",
					error,
				);
			}
		};

		setupWebSocket();

		// Handle session expiry notification from server
		const handleSessionExpired = (data: {
			sessionId: string;
			reason: string;
		}) => {
			if (data.sessionId === sessionId) {
				console.warn("[useUserChat] Session expired:", data.reason);
				// Keep the transcript on screen and say the chat ended; the
				// user starts a new one from the notice ("New chat").
				setIsSessionEnded(true);
			}
		};

		onSocketEvent(
			"chat:session:expired",
			handleSessionExpired,
			GATEWAY_TYPES.CHAT,
		);

		return () => {
			offSocketEvent(
				"chat:session:expired",
				handleSessionExpired,
				GATEWAY_TYPES.CHAT,
			);
		};
	}, [sessionId]);

	// No close on unload/tab close. The stored session id is shared by every
	// tab, so one tab's reload or close used to CLOSE the chat under the others
	// and cancel an open request for a person, and a reload lost the
	// transcript (live-handoff browser QA 2026-09-29). A session now ends only
	// by an explicit Clear / New chat / close, or by server-side expiry
	// (`chat:session:expired`, handled above).

	// Check health status
	const checkHealth = async () => {
		try {
			const healthy = await userChatApi.checkHealth();
			setIsHealthy(healthy);
		} catch {
			setIsHealthy(false);
		}
	};

	// Load chat sessions
	const loadSessions = async () => {
		try {
			const sessionList = await userChatApi.getSessions();
			setSessions(sessionList);
		} catch (error) {
			console.error("Failed to load sessions:", error);
		}
	};

	// Send message to chat
	const sendMessage = useCallback(
		async (messageText: string) => {
			if (!messageText.trim()) return;

			// Report activity to WebSocket (resets inactivity timer)
			const socket = getSocket(GATEWAY_TYPES.CHAT);
			if (socket?.connected) {
				socket.emit("chat:activity");
			}

			// Add user message immediately for better UX
			const userMessage: ChatMessage = {
				id: `user-${Date.now()}`,
				role: "user",
				content: messageText,
				timestamp: new Date().toISOString(),
			};

			setMessages((prev) => [...prev, userMessage]);
			setIsLoading(true);

			try {
				const request: UserChatRequest = {
					message: messageText,
					session_id: sessionId,
				};

				const response: UserChatResponse =
					await userChatApi.sendMessage(request);

				// Update session ID if new
				if (!sessionId && response.session_id) {
					setSessionId(response.session_id);
					// Reload sessions to include the new one
					loadSessions();
				}

				if (response.handoff !== undefined || response.handoff_error) {
					applyServerHandoff(response.handoff, response.handoff_error ?? null);
				}

				// Add assistant response
				const assistantMessage: ChatMessage = {
					id: `assistant-${Date.now()}`,
					role: "assistant",
					content: response.response,
					timestamp: response.timestamp,
					metadata: {
						used_knowledge_base: response.used_knowledge_base,
						documents_found: response.documents_found,
						generation_time_ms: response.generation_time_ms,
					},
				};

				setMessages((prev) => [...prev, assistantMessage]);
			} catch (error: any) {
				console.error("Chat error:", error);
				if (error?.name === "SessionEndedError") {
					markSessionEnded();
				} else {
					message.error(error.message || "Failed to send message");
				}

				// Remove the user message if the request failed
				setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
			} finally {
				setIsLoading(false);
			}
		},
		[sessionId, markSessionEnded],
	);

	// Send message with streaming response. Returns whether the message was
	// ACCEPTED — false means the busy-guard dropped it (caller must not clear
	// its input; silently eating sends was a real user-facing bug: the fetch
	// promise resolves only when the SSE connection closes, which can lag the
	// visible end of the turn, so this guard used to swallow fast follow-ups).
	const sendMessageStream = useCallback(
		async (messageText: string): Promise<boolean> => {
			if (!messageText.trim() || isLoading || isStreaming || isSessionEnded) {
				return false;
			}

			// A previous turn's connection can still be draining after its
			// complete event already rendered (isStreaming is flipped at the
			// semantic end of turn in onComplete below). Abort any lingering
			// connection before starting the new turn.
			abortControllerRef.current?.abort();

			// Report activity to WebSocket (resets inactivity timer)
			const socket = getSocket(GATEWAY_TYPES.CHAT);
			if (socket?.connected) {
				socket.emit("chat:activity");
			}

			// Add user message immediately for better UX
			const userMessage: ChatMessage = {
				id: `user-${Date.now()}`,
				role: "user",
				content: messageText,
				timestamp: new Date().toISOString(),
			};

			// Create placeholder for streaming assistant response. Skipped while a
			// staff member has the chat: the AI is paused and will not answer, and
			// the person's reply arrives over the socket instead.
			const assistantMessageId = `assistant-${Date.now()}`;
			const assistantMessage: ChatMessage = {
				id: assistantMessageId,
				role: "assistant",
				content: "",
				timestamp: new Date().toISOString(),
			};

			setMessages((prev) =>
				isHumanActiveRef.current
					? [...prev, userMessage]
					: [...prev, userMessage, assistantMessage],
			);
			setIsStreaming(true);

			// Create abort controller for cancellation. Kept in a local so the
			// finally/onComplete of THIS turn never clobbers state owned by a
			// newer turn (see the lingering-connection abort above).
			const controller = new AbortController();
			abortControllerRef.current = controller;

			try {
				const request: UserChatRequest = {
					message: messageText,
					session_id: sessionId,
				};

				await userChatApi.sendMessageStream(
					request,
					{
						onStart: (event: StreamingChatEvent) => {
							// Update session ID if provided
							if (event.data?.session_id && !sessionId) {
								setSessionId(event.data.session_id);
								localStorage.setItem(
									sessionKey(sessionCompanyId),
									event.data.session_id,
								);
								loadSessions();
							}
						},
						onSessionEnded: () => {
							setMessages((prev) =>
								dropEmptyPlaceholder(prev, assistantMessageId),
							);
							markSessionEnded();
						},
						onSessionCompanyMismatch: () => {
							// The stored session belongs to another company (one
							// saved before sessions were keyed per company). Start
							// a fresh one; userChatApi re-sends this turn in it.
							const freshSessionId = uuidv4();
							setSessionId(freshSessionId);
							localStorage.setItem(
								sessionKey(sessionCompanyId),
								freshSessionId,
							);
							return freshSessionId;
						},
						onHandoff: (handoff, error) => {
							applyServerHandoff(handoff, error);
						},
						onStatus: (status) => {
							// A person has the chat (the API/recon-ai gate saw a
							// paused session): no AI text is coming for this turn.
							if (status !== "human_agent") return;
							setMessages((prev) =>
								dropEmptyPlaceholder(prev, assistantMessageId),
							);
							// The server says a person has the chat but local state
							// says the AI does: a push was missed. Resync.
							if (!isHumanActiveRef.current) {
								void refreshHandoff();
								void refreshTranscript();
							}
						},
						onBlock: (block) => {
							// Append a typed content block (e.g. citation) to the
							// streaming assistant message; renders after the text.
							setMessages((prev) =>
								prev.map((msg) =>
									msg.id === assistantMessageId
										? { ...msg, blocks: [...(msg.blocks ?? []), block] }
										: msg,
								),
							);
						},
						onToken: (token: string) => {
							// Update the assistant message content progressively
							setMessages((prev) =>
								prev.map((msg) =>
									msg.id === assistantMessageId
										? { ...msg, content: msg.content + token }
										: msg,
								),
							);
						},
						onError: (error: string) => {
							console.error("Stream error:", error);
							const errorText =
								error || "The assistant hit an error. Please try again.";
							message.error(errorText);
							// Keep the assistant bubble and render the error into it so the
							// failed turn stays in the transcript. Removing it left the user
							// staring at their own question with no response once the toast
							// faded (e.g. a 60s data-tool timeout). Preserve any tokens that
							// already streamed before the failure. Guard on `isError` so a
							// stream that fires onError more than once (retries) doesn't stack
							// duplicate error lines into the bubble.
							setMessages((prev) =>
								prev.map((msg) => {
									if (msg.id !== assistantMessageId || msg.isError) return msg;
									return {
										...msg,
										isError: true,
										content: msg.content
											? `${msg.content}\n\n${errorText}`
											: errorText,
									};
								}),
							);
						},
						onComplete: (event: StreamingChatEvent) => {
							// The turn is semantically over the moment `complete`
							// arrives — reopen the send gate now instead of waiting for
							// the SSE connection to close (which lags in `finally` and
							// was silently swallowing fast follow-up sends).
							setIsStreaming(false);
							// A turn went through, so an earlier "asked too often"
							// banner no longer describes the chat.
							clearHandoffError();

							// Finalize message with metadata
							setMessages((prev) =>
								prev.map((msg) =>
									msg.id === assistantMessageId
										? {
												...msg,
												metadata: {
													used_knowledge_base: event.data?.context_used,
													documents_found: event.data?.documents_found,
													generation_time_ms: event.data?.timing?.total_time_ms,
												},
											}
										: msg,
								),
							);

							// Update session ID if provided in complete event
							if (event.session_id && !sessionId) {
								setSessionId(event.session_id);
								localStorage.setItem(
									sessionKey(sessionCompanyId),
									event.session_id,
								);
								loadSessions();
							}
						},
					},
					abortControllerRef.current.signal,
					companyId,
				);
			} catch (error: any) {
				// Don't show error for intentional abort
				if (error?.name !== "AbortError") {
					console.error("Chat stream error:", error);
					// This catch only ever sees TRANSPORT failures (the SSE socket
					// died, DNS/connection refused, a non-OK open). Their `message`
					// is library text with no meaning to a customer — the observed
					// one was "The socket connection was closed unexpectedly. For
					// more information, pass `verbose: true` in the second argument
					// to fetch()". Anything the backend wants the user to read
					// arrives as an `error` FRAME and is handled by `onError` above,
					// so say something human here and keep the raw text in the log.
					const errorText =
						"The assistant lost its connection. Please try again.";
					message.error(errorText);
					// Keep BOTH messages and render the failure into the assistant
					// bubble — same treatment `onError` above already gets. Deleting
					// them took the user's own question out of the transcript, so a
					// truncated stream (upstream `IncompleteRead`, ~10% of turns) read
					// as "my message vanished" once the toast faded, with nothing left
					// to retry from. Preserve any tokens that streamed before the
					// failure, and don't stack a second error line onto a bubble
					// `onError` already marked.
					setMessages((prev) =>
						prev.map((msg) => {
							if (msg.id !== assistantMessageId || msg.isError) return msg;
							return {
								...msg,
								isError: true,
								content: msg.content
									? `${msg.content}\n\n${errorText}`
									: errorText,
							};
						}),
					);
				}
			} finally {
				// Only reset shared state if THIS turn still owns the controller. A
				// fast follow-up (accepted once onComplete reopened the gate) installs
				// a newer controller; this turn's late-draining connection must not
				// clobber the newer turn's isStreaming/controller state.
				if (abortControllerRef.current === controller) {
					setIsStreaming(false);
					abortControllerRef.current = null;
				}
			}

			// Past the busy-guard, the turn was accepted and started (this also runs
			// after the catch — an attempted send counts as accepted since the user
			// already got transcript/toast feedback).
			return true;
		},
		[
			sessionId,
			isLoading,
			isStreaming,
			companyId,
			sessionCompanyId,
			refreshHandoff,
			refreshTranscript,
			applyServerHandoff,
			clearHandoffError,
			isSessionEnded,
			markSessionEnded,
		],
	);

	// Drop a server-derived action proposal into the transcript as an assistant
	// message carrying a single `action` confirm-card block. Used by the
	// results-page "Propose for approval" path (via the window "equility:chat:propose"
	// event): the FE already assembled the exact amend payload, so the card renders
	// with a real ledger-backed proposalId/idempotencyKey instead of an LLM guess.
	// Routed through safeParseBlock so it's validated exactly like a streamed block
	// (a drifted shape degrades to UnsupportedBlock rather than a fake approvable
	// card). Returns the appended message id.
	const appendActionProposal = useCallback(
		(proposal: IChatActionProposal): string => {
			const block = safeParseBlock({
				id: `action-${proposal.proposalId}`,
				type: "action",
				schemaVersion: CHAT_BLOCK_SCHEMA_VERSION,
				status: "complete",
				proposalId: proposal.proposalId,
				idempotencyKey: proposal.idempotencyKey,
				title: proposal.title,
				// The api sends "amend-to-match"; fall back to the same constant so the
				// block always has a string workflow (else safeParseBlock degrades it to
				// UnsupportedBlock). Mirrors recon-ai's action_proposal_to_block.
				workflow: proposal.workflow ?? "amend-to-match",
				preview: proposal.preview,
				...(proposal.summaryAmount
					? { summaryAmount: proposal.summaryAmount }
					: {}),
				...(proposal.expiresAt ? { expiresAt: proposal.expiresAt } : {}),
				...(proposal.asOf ? { asOf: proposal.asOf } : {}),
			});

			const messageId = `assistant-${Date.now()}`;
			const assistantMessage: ChatMessage = {
				id: messageId,
				role: "assistant",
				content: "Here's the amend I prepared. Review and confirm:",
				timestamp: new Date().toISOString(),
				blocks: [block],
			};
			setMessages((prev) => [...prev, assistantMessage]);
			return messageId;
		},
		[],
	);

	// Stop streaming response
	const stopStreaming = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			setIsStreaming(false);
		}
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	// Create new session
	const createNewSession = useCallback(
		async (name?: string) => {
			try {
				const session = await userChatApi.createSession(name);
				const newSessionId = session.id;
				setSessionId(newSessionId);
				localStorage.setItem(sessionKey(sessionCompanyId), newSessionId);
				setMessages([]);
				await loadSessions();
				return session;
			} catch (error: any) {
				message.error(error.message || "Failed to create session");
				throw error;
			}
		},
		[sessionCompanyId],
	);

	// Switch to existing session
	const switchSession = useCallback(
		(newSessionId: string) => {
			// Same rule as Clear / New chat: leaving would strand a request for
			// a person on a chat the customer can no longer see.
			if (isHandoffOpen(handoffState.handoff)) {
				message.warning(
					"Finish or cancel your chat with the Equility team first.",
				);
				return;
			}
			setSessionId(newSessionId);
			localStorage.setItem(sessionKey(sessionCompanyId), newSessionId);
			// Cleared here; the transcript effect reloads it for the new id.
			setMessages([]);
		},
		[sessionCompanyId, handoffState.handoff],
	);

	// Clear the conversation AND start a fresh backend session. Reusing the same
	// session id leaves prior turns in the model's context, so one ungrounded
	// turn (a hallucinated/"no data access" reply) poisons later answers with no
	// in-UI way to recover. "Clear" now rotates the session so the next message
	// starts a clean turn 1; the old session is closed best-effort so it isn't
	// orphaned.
	const clearConversation = useCallback(() => {
		setMessages([]);
		setIsSessionEnded(false);
		if (sessionId) {
			void userChatApi.closeSession(sessionId).catch(() => {});
		}
		localStorage.removeItem(sessionKey(sessionCompanyId));
		const newSessionId = uuidv4();
		setSessionId(newSessionId);
		localStorage.setItem(sessionKey(sessionCompanyId), newSessionId);
	}, [sessionId, sessionCompanyId]);

	// Delete a session
	const deleteSession = useCallback(
		async (sessionIdToDelete: string) => {
			try {
				await userChatApi.deleteSession(sessionIdToDelete);

				if (sessionIdToDelete === sessionId) {
					clearConversation();
				}

				await loadSessions();
				message.success("Session deleted");
			} catch (error: any) {
				message.error(error.message || "Failed to delete session");
			}
		},
		[sessionId, sessionCompanyId],
	);

	// Close current session (marks as complete, user-initiated)
	const closeCurrentSession = useCallback(async () => {
		if (!sessionId) return;

		try {
			// Close via REST API
			await userChatApi.closeSession(sessionId);

			// Notify WebSocket gateway
			const socket = getSocket(GATEWAY_TYPES.CHAT);
			if (socket?.connected) {
				socket.emit("chat:session:close", { sessionId });
			}

			// Clear local state and create new session
			localStorage.removeItem(sessionKey(sessionCompanyId));
			setSessionId(undefined);
			setMessages([]);

			// Generate new session for future use
			const newSessionId = uuidv4();
			setSessionId(newSessionId);
			localStorage.setItem(sessionKey(sessionCompanyId), newSessionId);

			message.success("Session closed");
			await loadSessions();
		} catch (error: any) {
			// Best-effort close - don't show error to user
			console.error("Failed to close session:", error);
		}
	}, [sessionId, sessionCompanyId]);

	return {
		messages,
		isLoading,
		isStreaming,
		sessionId,
		sessions,
		isHealthy,
		sendMessage,
		sendMessageStream,
		appendActionProposal,
		stopStreaming,
		createNewSession,
		switchSession,
		clearConversation,
		deleteSession,
		closeCurrentSession,
		refreshSessions: loadSessions,
		isSessionEnded,
		// Live staff handoff: handoff, isRequestingHuman, isAwaitingHuman,
		// isHumanActive, requestHuman, cancelHuman.
		...handoffState,
	};
}
